import {
    Box,
    chakra,
    Flex,
    FormControl,
    FormLabel,
    Input,
    Select,
    Text,
    Textarea
} from '@chakra-ui/react';
import { AxiosError } from 'axios';
import { useCurrentUser } from 'context/currentUserContext';
import { useSession } from 'next-auth/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { SubmitHandler } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useHookForm } from '@/hooks/useHookForm';
import { useToast } from '@/hooks/useToast';

import { ApiErrorMessage, ApiErrors } from '@/components/ApiErrors/ApiErrors';
import { FormControlError } from '@/components/FormControlError/FormControlError';
import { Button } from '@/components/UI/Buttons/Button';

import { ValidationKey } from '@/constants/validation-key';
import { ContactForm } from '@/models/api/Contact';
import { ContactService } from '@/services';

import { ContactAgreements } from './ContactAgreements/ContactAgreements';
import { useFileUpload } from './useFileUpload';
import { AttachIcon } from '../Icons/AttachIcon';

export const ContactUserForm = () => {
    const MESSAGE_FIELD_MAX_LENGTH = 5000;
    const contactForm = useHookForm<ContactForm>();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const {
        clearUploadErrorMessage,
        fileInputAcceptAttribute,
        fileRef,
        handleFileRemove,
        handleFileUpload,
        attachment,
        uploadErrorMessage
    } = useFileUpload();
    const intl = useIntl();
    const { errorToast, successToast } = useToast();
    const [userEmail, setUserEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const { formState, register, handleSubmit } = contactForm;
    const emailRef = useRef<HTMLInputElement>(null);
    const { data: session } = useSession();
    const { currentUser } = useCurrentUser();

    const subjectField = useRef<HTMLSelectElement>(null);
    const [apiErrors, setApiErrors] = useState<ApiErrorMessage[]>([]);

    const contentLength = contactForm.watch('content')?.length || 0;

    const prepareContactFormData = (data: ContactForm) => {
        const sendData: ContactForm = {
            subject:
                intl.formatMessage({
                    id: options[subjectField.current?.selectedIndex as number]
                }) || '',
            content: data.content,
            email: emailRef.current?.value || data.email
        };
        if (attachment) {
            sendData.attachments = [attachment];
        }
        return sendData;
    };

    const onSubmit: SubmitHandler<ContactForm> = async (data) => {
        if ((attachment && uploadErrorMessage) || !executeRecaptcha) return;
        clearUploadErrorMessage();

        setIsSubmitting(true);

        const sendData = prepareContactFormData(data);

        const recaptchaData = await executeRecaptcha('enquiryFormSubmit');
        const reCaptchaRes = await ContactService.verifyRecaptcha(recaptchaData);

        if (reCaptchaRes.success) {
            try {
                await ContactService.sendContactForm(sendData);

                successToast({
                    title: intl.formatMessage({ id: 'contact.message-sent' })
                });

                handleFileRemove();
                contactForm.reset();
                setApiErrors([]);
            } catch (error) {
                if (error instanceof AxiosError) {
                    setApiErrors(error?.response?.data?.errors);
                }

                errorToast({
                    title: intl.formatMessage({ id: 'contact.message-sent.error' })
                });
            } finally {
                setIsSubmitting(false);
            }
        } else {
            errorToast({
                title: intl.formatMessage({ id: 'contact.message-sent.error' })
            });
            setIsSubmitting(false);
        }
    };

    let emailControl = register('email', {
        required: {
            value: true,
            message: ValidationKey.FieldIsRequired
        },
        pattern: {
            value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            message: ValidationKey.PleaseEnterValidEmail
        }
    });

    if (session) {
        emailControl = register('email', {
            required: {
                value: false,
                message: ValidationKey.FieldIsRequired
            }
        });
    }

    const messageControl = register('content', {
        required: {
            value: true,
            message: ValidationKey.FieldIsRequired
        },
        maxLength: {
            value: MESSAGE_FIELD_MAX_LENGTH,
            message: intl.formatMessage({ id: 'maxLength' }, { num: MESSAGE_FIELD_MAX_LENGTH })
        }
    });

    const options = useMemo(() => {
        return [
            'contact.subject.option.one',
            'contact.subject.option.two',
            'contact.subject.option.three',
            'contact.subject.option.four',
            'contact.subject.option.five'
        ];
    }, []);

    useEffect(() => {
        if (currentUser?.address.email) setUserEmail(currentUser.address.email);
    }, [currentUser]);

    return (
        <>
            <ApiErrors errors={apiErrors} mb={4} />
            <chakra.form noValidate onSubmit={handleSubmit(onSubmit)}>
                <Box marginBottom={3}>
                    <FormLabel textAlign="left" fontWeight="bold" marginBottom="2">
                        <FormattedMessage id="contact.subject" />
                    </FormLabel>
                    <Select ref={subjectField} borderColor="grey.400" minH={12}>
                        {options.map((item, index) => (
                            <option key={index} value={`option${index + 1}`}>
                                <FormattedMessage id={item} />
                            </option>
                        ))}
                    </Select>
                </Box>
                <Box marginBottom={3}>
                    <FormControl isInvalid={!!formState.errors[emailControl.name]} isRequired>
                        <FormLabel textAlign="left" fontWeight="bold" marginBottom="2">
                            <FormattedMessage id="contact.email" />
                        </FormLabel>
                        {userEmail && (
                            <FormControlError formState={formState} control={emailControl}>
                                <Input
                                    {...emailControl}
                                    type="email"
                                    value={userEmail}
                                    readOnly={true}
                                    isDisabled={true}
                                    minH={12}
                                    ref={emailRef}
                                />
                            </FormControlError>
                        )}
                        {!userEmail && (
                            <FormControlError formState={formState} control={emailControl}>
                                <Input type="email" minH={12} {...emailControl} />
                            </FormControlError>
                        )}
                    </FormControl>
                </Box>
                <Box position="relative" marginBottom={3}>
                    <FormLabel
                        display={attachment ? 'none' : ''}
                        cursor="pointer"
                        color="blue.500"
                        textAlign="left"
                        fontWeight="bold"
                        marginBottom="3"
                    >
                        <Input
                            ref={fileRef}
                            padding={0}
                            border={0}
                            width={0}
                            height={0}
                            type="file"
                            accept={fileInputAcceptAttribute}
                            onChange={handleFileUpload}
                        />
                        <AttachIcon marginRight={3} />
                        <FormattedMessage id="contact.add-file" />
                    </FormLabel>
                    <Box
                        display={attachment ? 'flex' : 'none'}
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Box color="blue.500" wordBreak="break-all">
                            {attachment?.name}
                        </Box>
                        <Button onClick={handleFileRemove} colorScheme="blue" marginLeft={2}>
                            <FormattedMessage id="contact.remove" />
                        </Button>
                    </Box>
                    {uploadErrorMessage && (
                        <Box className="errorFile" fontSize="xs" color="red.400">
                            <FormattedMessage
                                id={uploadErrorMessage.code}
                                values={uploadErrorMessage.values}
                            />
                        </Box>
                    )}
                </Box>
                <Box marginBottom={3}>
                    <FormControl isInvalid={!!formState.errors[messageControl.name]} isRequired>
                        <Flex>
                            <FormLabel textAlign="left" fontWeight="bold" marginBottom="2">
                                <FormattedMessage id="contact.message" />
                            </FormLabel>
                            <Text ml="auto">
                                {contentLength}/{MESSAGE_FIELD_MAX_LENGTH}
                            </Text>
                        </Flex>
                        <FormControlError formState={formState} control={messageControl}>
                            <Textarea
                                borderRadius="21px"
                                minHeight="100px"
                                maxLength={MESSAGE_FIELD_MAX_LENGTH}
                                {...messageControl}
                            />
                        </FormControlError>
                    </FormControl>
                </Box>
                <ContactAgreements formInstance={contactForm} />
                <Box textAlign="right">
                    <Button
                        type="submit"
                        colorScheme="blue"
                        size="lg"
                        rounded="full"
                        isLoading={isSubmitting}
                    >
                        <FormattedMessage id="send" />
                    </Button>
                    <Text
                        fontSize="2xs"
                        marginTop={2}
                        dangerouslySetInnerHTML={{
                            __html: intl.formatMessage({ id: 'contact.required-fields-info' })
                        }}
                    />
                </Box>
            </chakra.form>
        </>
    );
};

export default ContactUserForm;
